/**
 * Email Deferred / Pending-Retry status helpers
 *
 * SendGrid retries deferred emails for up to 72 hours, then either delivers,
 * hard-bounces, or silently gives up. When SendGrid silently gives up (or a
 * final webhook is missed), recipients that received a `deferred` event with
 * no follow-up event would otherwise appear as "Pending Retry" forever.
 *
 * This helper closes the window: if the most recent `deferred` event is older
 * than the SendGrid retry window plus a small buffer, the recipient is treated
 * as `expired` instead of still pending. The buffer accounts for clock drift
 * and final-status events that may arrive shortly after the 72h mark.
 */

// SendGrid's documented max retry window for deferred messages.
export const SENDGRID_RETRY_WINDOW_MS = 72 * 60 * 60 * 1000;
// Grace period beyond the 72h window before flipping to "expired".
export const RETRY_WINDOW_BUFFER_MS = 6 * 60 * 60 * 1000;
// Effective threshold (78 hours).
export const PENDING_RETRY_THRESHOLD_MS = SENDGRID_RETRY_WINDOW_MS + RETRY_WINDOW_BUFFER_MS;

/**
 * Inspect a list of email events and classify each deferred recipient as
 * either still-pending or expired.
 *
 * @param {Array} events - Email event objects with `eventType`, `recipientEmail`, `timestamp` (Date).
 * @param {number} [now] - Override for "current time" in ms (defaults to Date.now()).
 * @returns {{
 *   pendingRetryEmails: Set<string>,
 *   expiredDeferredEmails: Set<string>,
 *   lastDeferredByEmail: Map<string, number>
 * }}
 */
export function buildDeferredStatus(events, now = Date.now()) {
  const lastDeferredByEmail = new Map();
  const resolvedByEmail = new Set();

  for (const e of events) {
    const email = e?.recipientEmail;
    if (!email) continue;

    if (e.eventType === 'deferred') {
      const ts = e.timestamp instanceof Date
        ? e.timestamp.getTime()
        : (typeof e.timestamp === 'number' ? e.timestamp : null);
      if (ts != null) {
        const prev = lastDeferredByEmail.get(email) || 0;
        if (ts > prev) lastDeferredByEmail.set(email, ts);
      }
    }

    if (
      e.eventType === 'delivered' ||
      e.eventType === 'bounced' ||
      e.eventType === 'blocked' ||
      e.eventType === 'dropped'
    ) {
      resolvedByEmail.add(email);
    }
  }

  const pendingRetryEmails = new Set();
  const expiredDeferredEmails = new Set();

  lastDeferredByEmail.forEach((ts, email) => {
    if (resolvedByEmail.has(email)) return;
    if (now - ts <= PENDING_RETRY_THRESHOLD_MS) {
      pendingRetryEmails.add(email);
    } else {
      expiredDeferredEmails.add(email);
    }
  });

  return { pendingRetryEmails, expiredDeferredEmails, lastDeferredByEmail };
}
